import { useEffect } from "react";
import { createPortal } from "react-dom";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

/**
 * Slide-up bottom sheet rendered via a React portal directly into #root.
 *
 * Portalling to #root (which has position:relative + overflow:hidden) means
 * absolute inset-0 always covers the entire phone shell — including the header
 * and tab bar — regardless of where in the component tree this is rendered.
 * Without the portal, sheets rendered inside the tab-content wrapper (which has
 * its own position:relative) would only cover that inner area, leaving the
 * header and tab bar visible outside the backdrop.
 */
export function BottomSheet({ open, onClose, title, children }: BottomSheetProps) {
  // Prevent body scroll while open.
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const root = document.getElementById("root") ?? document.body;

  return createPortal(
    <div className="absolute inset-0 z-50 flex flex-col justify-end">
      {/* Full-shell backdrop — covers header + content + tab bar */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Panel — naturally sized to content, mx-3 side margins, flush to bottom */}
      <div className="relative z-10 mx-2 flex max-h-[80%] flex-col rounded-t-2xl bg-card shadow-2xl">
        {/* Drag handle */}
        <div className="flex shrink-0 justify-center pb-1 pt-3">
          <div className="h-1 w-9 rounded-full bg-border" />
        </div>

        {/* Header row */}
        <div className="flex shrink-0 items-center justify-between px-5 pb-4 pt-2">
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Close
          </button>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    root,
  );
}
